import { debounce } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { EditableTextFilterCard } from 'components/EditableTextFilterCard'
import {
  StaleFilterValuesNotice,
  type StaleFilterValuesNoticeProps,
} from 'components/StaleFilterValuesNotice'
import { FilterCard } from 'features/charts-v2/components/FilterCard'
import {
  type ExternalPopoverTrigger,
  FilterSelectHeader,
} from 'features/charts-v2/components/FilterSelectHeader'
import { FilterCard as KeywordCard } from 'features/charts-v2/components/KeywordCard'
import { type OperatorType as KeywordOperatorType } from 'features/charts-v2/components/KeywordOperatorPopover'
import { NumericFilterCard } from 'features/charts-v2/components/NumericFilterCard'
import {
  type FilterCardEntry,
  SelectedFilterOptionList,
} from 'features/charts-v2/components/SelectedFilterOptionList'
import {
  SentimentFilterCard,
  type SentimentMode,
} from 'features/charts-v2/components/SentimentFilterCard'
import { FilterCardsSkeleton } from 'features/charts-v2/components/skeletons'
import {
  type FilterSelectionDraft,
  isNumericFilterField,
  NUMERIC_FILTER_LABELS,
  type NumericFilterFieldType,
  type NumericOperatorType,
  type OperatorType,
  type SelectedFilterOption,
} from 'features/charts-v2/types'
import type { FilterRestriction } from 'features/charts-v2/types/filter-restriction'
import type {
  InheritedFilterEntry,
  InheritedFilters,
} from 'features/charts-v2/types/inherited-filters'
import { deriveSentimentMode } from 'features/charts-v2/utils/derive-sentiment-mode'
import { FILTER_GROUP_LABELS } from 'features/charts-v2/utils/filter-sidebar-groups'
import { KEYWORD_OPERATORS } from 'features/charts-v2/utils/keyword-operators'
import { restrictFilterOptions } from 'features/charts-v2/utils/restrict-filter-options'
import {
  FilterOption,
  FilterTypes,
  type YogiQLFilterPreset,
} from 'features/filters/types'
import { FilterPreset } from 'types/project'

const SENTIMENT_FIELDS = new Set(['sentiment', 'post_sentiment'])

export interface FilterControlProps {
  filterOptions: FilterOption[]
  isFilterOptionsLoading?: boolean
  // Current filter selections - needed to trigger re-renders when filters change
  selectedFilterOptions: SelectedFilterOption[]
  updateFilterSelection: (
    field: string,
    operator: OperatorType | NumericOperatorType,
    values: string[],
    rangeData?: {
      min?: number
      max?: number
      valueType?: number
      numericValue?: number
    },
  ) => void
  getFilterSelection: (field: string) => SelectedFilterOption | null
  // Reports the popover field whose counts should be fetched up to the parent,
  // so the per-panel V2 counts fetch stays scoped to one field. `null` when no
  // field is expanded (popover closed or "All Properties" view). PROD-3136.
  onSelectedFieldChange?: (field: string | null) => void
  // Looks up a field's stashed (eye-toggled-off) definition, if any. A ghosted
  // field has no live selection but must still render a card. Omit all three
  // ghost props to hide the eye/disable toggle entirely for this consumer.
  getGhostSelection?: (field: string) => SelectedFilterOption | null
  onToggleFilterDisabled?: (field: string) => void
  onRemoveGhost?: (field: string) => void
  // Looks up a field's stale values (present in the stored selection but no
  // longer offered by the project), if any. Omitted entirely renders no
  // stale badge on any card — see `FilterCard`'s `stale` doc comment.
  getStaleInfo?: (
    field: string,
  ) => { values: readonly string[]; labels: Record<string, string> } | undefined
  // The notice above the card list for the stale values in this control's own
  // filters — which values, what they do to the data, and a "Remove" that
  // clears them. Omitted renders no notice — see `buildStaleFilterControls`.
  staleValuesNotice?: StaleFilterValuesNoticeProps
  smartSearchValue: string | null | undefined
  keywordFilters: { [key: string]: string[] | null }
  updateSmartSearch: (searchValue: string | null | undefined) => void
  updateKeywordFilters: (filters: { [key: string]: string[] | null }) => void
  showBreakdownHierarchy?: boolean
  onSavedFilterApply?: (preset: FilterPreset) => void
  onSavedQueryApply?: (preset: YogiQLFilterPreset) => void
  showSyncButton?: boolean
  onSyncFilters?: () => void
  // When set, FilterSelectHeader portals its add-filter (and sync) buttons
  // here instead of rendering its own "Filter" heading row — used by
  // ControlsSidebar when 'filter' is the sidebar's only section, so the
  // buttons sit in the sidebar's own header row instead of duplicating it.
  headerActionsPortalTarget?: HTMLElement | null
  // The parent scope's (dashboard's) filters as they relate to this tile,
  // rendered as read-only cards alongside the tile's own. Omitted by every
  // caller that has no parent scope (`/charts`, Feedback, Keywords) — only
  // the dashboards-v2-1 sidebar hooks build and pass this.
  inherited?: InheritedFilters
  // Focuses the parent scope's own filter controls (e.g. the dashboard's
  // Global filters panel) — forwarded to every inherited card's eye/✕, which
  // no longer disable/remove the parent's filter (only editing a card's
  // VALUES can override it), and to every accent bar's "Edit global
  // filters" link. Omitted by the same callers that omit `inherited` — see
  // `SelectedFilterOptionList`'s doc comment.
  onEditInheritedSource?: () => void
  // Which word ("chart"/"widget") an accent bar's LOCAL copy uses — see
  // `FilterCardAccentBar`'s doc comment. Omitted by the same callers that
  // omit `inherited` (there's no local accent bar to label outside a
  // dashboard tile).
  tileKind?: 'chart' | 'widget'
  // The accent bar for rows this control OWNS. Defaults to 'local' whenever
  // `inherited` is present (a dashboard tile, which sits under a parent
  // scope) and to no bar at all when it is absent (`/charts`, Feedback,
  // Keywords — no scope chain to signal). Set explicitly to 'global' by the
  // dashboard's global filter bar, whose own filters ARE the global scope:
  // it passes a non-undefined-but-empty `inherited` purely so `restriction`
  // can be derived, so the default above would mislabel its cards 'local'.
  ownedAccent?: 'local' | 'global'
  // Which values within each field this tile's picker may select, derived
  // from `inherited` via `deriveFilterRestriction` — a tile may only narrow
  // the dashboard's filters, never broaden past them. Omitted by the same
  // callers that omit `inherited`, and by an `isUnlinked` widget tile (see
  // `deriveFilterRestriction`'s doc comment).
  restriction?: FilterRestriction
  // Forwarded to every generic FilterCard — see its `lockOperator` doc
  // comment. Only affects the generic-card list; the WINDOW/keyword/numeric/
  // sentiment variants either already render the operator as static text or
  // have no operator axis to lock.
  lockOperator?: boolean
  // Overrides SelectedFilterOptionList's default `calc(100vh - 120px)` scroll
  // cap, for a mount that isn't a fixed-height sidebar. `undefined` keeps
  // today's behavior.
  listMaxHeight?: string
  // Names a selected value the filter options can't — e.g. a saved theme
  // uuid whose cluster the options omit. Forwarded to every generic
  // FilterCard; see its `presentation.getValueLabel` doc comment.
  getValueLabel?: (field: string, value: string) => string | undefined
  // Forwarded to FilterSelectHeader — see its `hideHeaderRow` doc comment.
  hideFilterHeaderRow?: boolean
  // When true, the dashed "+ Add filters" affordance stays visible even
  // once filters exist, instead of only when the list is empty — the
  // permanent add-entry-point for a caller that hides the header row above
  // (there would otherwise be no way to add a second filter). Default false:
  // no existing caller changes.
  alwaysShowAddFilters?: boolean
  // Forwarded to FilterSelectHeader — see its `emptyStateLabel` doc comment.
  emptyStateLabel?: string
  // Forwarded to FilterSelectHeader — see its `emptyStateVariant` doc comment.
  emptyStateVariant?: 'block' | 'inline'
  // Suppresses all six card-kind render blocks below (generic, inherited,
  // keyword, numeric, sentiment, smart-search) — only the add-filter
  // affordance (FilterSelectHeader) renders. For a caller that renders its
  // own card presentation for applied filters (e.g. removable chiclets) and
  // only needs FilterControl for the add-filter popover. Default undefined:
  // no existing caller changes.
  hideCardList?: boolean
  // Forwarded to FilterSelectHeader → FilterSelectionPopover — see the
  // latter's `anchorMode` doc comment.
  filterPopoverAnchorMode?: 'right' | 'center'
  // Forwarded to FilterSelectHeader → FilterSelectionPopover — see the
  // latter's `popoverPortalTarget` doc comment.
  filterPopoverPortalTarget?: HTMLElement | null
  // Forwarded to FilterSelectHeader → FilterSelectionPopover — see the
  // latter's `fieldsOnlySidebar` doc comment.
  filterPopoverFieldsOnlySidebar?: boolean
  // Forwarded to FilterSelectHeader → FilterSelectionPopover as
  // `disabledFields` — rows the picker greys out (a dashboard widget tile
  // can't use Records / Sentences / Rating / Sentiment). Default undefined:
  // no existing caller changes.
  filterPopoverDisabledFields?: { fields: ReadonlySet<string>; note: string }
  // Forwarded to FilterSelectHeader → FilterSelectionPopover as
  // `onApplySelections` — when set, the add-filter popover's checkbox lists
  // draft instead of committing per click (see that prop's doc comment).
  // Default undefined: every existing caller still commits per click.
  applyFilterSelections?: (selections: FilterSelectionDraft[]) => void
  // Where the FilterSelectHeader element (today, whatever of its
  // heading/buttons/"+ Add filters" empty state is showing per the flags
  // above) renders relative to the card list. 'before' (default) is today's
  // "add affordance on top" behavior for every existing caller. 'after'
  // puts already-applied cards first and the add affordance last.
  addFiltersRowPosition?: 'before' | 'after'
  // Lets a trigger outside this control (a "Filters" pill, a search bar) own
  // the add-filter popover's open state. When set, this control renders ONLY
  // that popover: no cards, no heading, no "+", no "+ Add filters" box — the
  // trigger's own UI is the whole surface. See FilterSelectHeader's
  // `externalTrigger` doc comment. Default undefined: no existing caller
  // changes.
  externalTrigger?: ExternalPopoverTrigger
}

export const FilterControl: React.FC<FilterControlProps> = ({
  filterOptions,
  isFilterOptionsLoading,
  selectedFilterOptions,
  updateFilterSelection,
  getFilterSelection,
  onSelectedFieldChange,
  getGhostSelection,
  onToggleFilterDisabled,
  onRemoveGhost,
  getStaleInfo,
  staleValuesNotice,
  smartSearchValue,
  keywordFilters,
  updateSmartSearch,
  updateKeywordFilters,
  showBreakdownHierarchy,
  onSavedFilterApply,
  onSavedQueryApply,
  showSyncButton,
  onSyncFilters,
  headerActionsPortalTarget,
  inherited,
  onEditInheritedSource,
  tileKind,
  ownedAccent,
  restriction,
  lockOperator,
  listMaxHeight,
  getValueLabel,
  hideFilterHeaderRow,
  alwaysShowAddFilters,
  emptyStateLabel,
  emptyStateVariant,
  hideCardList,
  filterPopoverAnchorMode,
  filterPopoverPortalTarget,
  filterPopoverFieldsOnlySidebar,
  filterPopoverDisabledFields,
  applyFilterSelections,
  addFiltersRowPosition = 'before',
  externalTrigger,
}) => {
  // The picker group each special card sits under, shown as its `Group > `
  // prefix. On unless `showBreakdownHierarchy` is explicitly false — the
  // default `FilterCard` uses, so one list never mixes prefixed and bare cards.
  const groupLabels =
    (showBreakdownHierarchy ?? true) ? FILTER_GROUP_LABELS : undefined

  // Local mirror of the smart-search text so typing stays responsive while the
  // panel update (which triggers a chart refetch) is debounced.
  const [smartSearchInput, setSmartSearchInput] = useState(
    smartSearchValue ?? '',
  )

  // Keep the local mirror in sync when the value changes externally (panel
  // switch, saved-filter apply, clear).
  useEffect(() => {
    setSmartSearchInput(smartSearchValue ?? '')
  }, [smartSearchValue])

  // Store the latest updateSmartSearch in a ref to avoid stale closures.
  const updateSmartSearchRef = useRef(updateSmartSearch)
  useEffect(() => {
    updateSmartSearchRef.current = updateSmartSearch
  }, [updateSmartSearch])

  // Debounce the panel update so a chart request fires once typing settles, not
  // on every keystroke. Mirrors the NumericFilterCard / CascadingDateMenu
  // debounce pattern in this feature.
  const debouncedUpdateSmartSearch = useRef(
    debounce((value: string) => {
      updateSmartSearchRef.current(value.length > 0 ? value : undefined)
    }, 300),
  ).current

  useEffect(() => {
    return () => {
      debouncedUpdateSmartSearch.cancel()
    }
  }, [debouncedUpdateSmartSearch])

  // The picker's option list, narrowed to what the dashboard's global
  // filters allow (see `restrictFilterOptions`'s doc comment for why this is
  // the single upstream choke point rather than N edits inside the popover).
  // No `selectedFilterOptions` dep needed: `getFilterSelection`'s closure
  // identity already changes whenever a selection does (same pattern
  // `filterEntries` below relies on for the same reason).
  const restrictedFilterOptions = useMemo(
    () => restrictFilterOptions(filterOptions, restriction, getFilterSelection),
    [filterOptions, restriction, getFilterSelection],
  )

  // The accent for every row this control owns — derived once so the four
  // render sites below (generic, smart search, keyword, sentiment) can't
  // drift apart. See `ownedAccent`'s doc comment for why `inherited`'s mere
  // presence isn't a reliable signal on its own.
  const ownedRowAccent = ownedAccent ?? (inherited ? 'local' : undefined)

  // One InheritedFilterEntry per facet field the parent scope filters on,
  // keyed by field. Sentiment fields are excluded — they render through the
  // unified SentimentFilterCard further down, same as the tile's own.
  const inheritedFacetsByField = useMemo(() => {
    const map = new Map<string, InheritedFilterEntry>()
    inherited?.facets.forEach((entry) => {
      if (!SENTIMENT_FIELDS.has(entry.selection.field)) {
        map.set(entry.selection.field, entry)
      }
    })
    return map
  }, [inherited])

  // The generic filter-card list: one row per (field, kind) pair, in
  // filterOptions order. A field the tile owns AND the parent scope filters
  // on produces two adjacent rows — the interleaved override case.
  // Sentiment fields (sentiment / post_sentiment / sentiment_filter) are
  // rendered as a single SentimentFilterCard below, so exclude them here.
  const filterEntries = useMemo<FilterCardEntry[]>(() => {
    const entries: FilterCardEntry[] = []
    const seenFields = new Set<string>()

    restrictedFilterOptions.forEach((filter) => {
      if (SENTIMENT_FIELDS.has(filter.field)) return
      seenFields.add(filter.field)

      // Global before local: when a field has both, the inherited (dashboard)
      // card renders first and the tile's own override follows.
      const inheritedEntry = inheritedFacetsByField.get(filter.field)
      if (inheritedEntry) {
        entries.push({
          kind: 'inherited',
          filter,
          entry: inheritedEntry,
          accent: 'global',
        })
      }

      const selection = getFilterSelection(filter.field)
      // A ghosted field (eye-toggled off) has no live selection but must
      // still render its card, struck through, so it can be re-enabled.
      if (selection !== null || getGhostSelection?.(filter.field) != null) {
        entries.push({
          kind: 'owned',
          filter,
          accent: ownedRowAccent,
          // Gated on `restriction`: an unsynced widget has an inherited
          // entry (shown crossed out) but `deriveFilterRestriction` returns
          // undefined for it, and its own picker must stay uncapped.
          globalSelection: restriction ? inheritedEntry?.selection : undefined,
        })
      }
    })

    // A field the parent scope filters on but this panel's own filter-option
    // catalogue doesn't carry still needs a card, rather than silently
    // vanishing — synthesize a minimal FilterOption for it.
    inheritedFacetsByField.forEach((entry, field) => {
      if (seenFields.has(field)) return
      entries.push({
        kind: 'inherited',
        filter: {
          field,
          alias: field,
          filter_type: FilterTypes.SELECT,
          values: [],
        },
        entry,
        accent: 'global',
      })
    })

    // The panel's own filter on a field the catalogue doesn't carry (Ask Yogi's
    // `brand_group`, opened under YogiQL) still applies, so it needs a card too,
    // after any inherited one. Its picker offers only the applied values.
    selectedFilterOptions.forEach(({ field }) => {
      const selection = getFilterSelection(field)
      if (
        seenFields.has(field) ||
        SENTIMENT_FIELDS.has(field) ||
        isNumericFilterField(field) ||
        selection === null
      ) {
        return
      }
      entries.push({
        kind: 'owned',
        filter: {
          field,
          alias: field,
          filter_type: FilterTypes.SELECT,
          values: selection.values.map((value) => ({ value, identifiers: [] })),
        },
        accent: ownedRowAccent,
        synthesized: true,
      })
    })

    return entries
  }, [
    restrictedFilterOptions,
    selectedFilterOptions,
    getFilterSelection,
    getGhostSelection,
    inheritedFacetsByField,
    ownedRowAccent,
    restriction,
  ])

  // Derive showSmartSearch from global state
  // null = filter not applied, undefined = applied but empty, string = applied with value
  const showSmartSearch = smartSearchValue !== null

  // Derive keywordSelections from keywordFilters prop
  const keywordSelections = useMemo(() => {
    const VALID_OPERATORS: KeywordOperatorType[] = [
      'Includes all',
      'Includes some',
      'Excludes some',
      'Excludes all',
    ]

    const selections: {
      [key in KeywordOperatorType]?: {
        field: string
        operator: KeywordOperatorType
        values: string[]
      }
    } = {}
    Object.entries(keywordFilters).forEach(([operator, values]) => {
      if (
        VALID_OPERATORS.includes(operator as KeywordOperatorType) &&
        values !== null
      ) {
        selections[operator as KeywordOperatorType] = {
          field: `keywords-${operator}`,
          operator: operator as KeywordOperatorType,
          values: values,
        }
      }
    })

    return selections
  }, [keywordFilters])

  // ── Early sentiment state (referenced by handleFilterAdd for exclusivity) ──
  // These are plain expressions (no hooks) so hoisting them before the
  // callback is safe. The full sentiment state block below re-derives the same
  // values alongside the rest of the sentiment handlers.
  const sentimentSelection = getFilterSelection('sentiment')
  const postSentimentSelection = getFilterSelection('post_sentiment')
  const sentimentFilterNumeric = selectedFilterOptions.find(
    (f) => f.field === 'sentiment_filter',
  )

  // Handle adding a new filter from the popover. `useCallback` keeps
  // the handler identity stable across re-renders so FilterSelectHeader's
  // `React.memo` barrier holds when no real input changed.
  const handleFilterAdd = useCallback(
    (
      field: string,
      values?: string[],
      rangeData?: { min?: number; max?: number; valueType?: number },
    ): void => {
      // Use provided values array or empty array
      const filterValues = values || []
      // Enforce sentiment exclusivity: selecting categorical sentiment values
      // clears any active numeric sentiment_filter (and vice versa is handled in
      // handleSentimentNumericUpdate).
      if (
        (field === 'sentiment' || field === 'post_sentiment') &&
        filterValues.length > 0 &&
        sentimentFilterNumeric
      ) {
        updateFilterSelection('sentiment_filter', 'greater than', [])
      }
      updateFilterSelection(field, 'is', filterValues, rangeData)
    },
    [updateFilterSelection, sentimentFilterNumeric],
  )

  // Handle keyword filter updates
  const handleKeywordFilterUpdate = (
    _field: string,
    operator: KeywordOperatorType,
    values: string[],
    oldOperator?: KeywordOperatorType,
  ): void => {
    const newFilters = { ...keywordFilters }

    // If oldOperator is provided and different from new operator, remove the old entry
    if (oldOperator && oldOperator !== operator) {
      newFilters[oldOperator] = null
    }

    // Always set the value (empty arrays keep card visible, API omits them)
    newFilters[operator] = values

    updateKeywordFilters(newFilters)
  }

  // Handle keyword filter deletion (explicit removal via delete button)
  const handleKeywordFilterDelete = (operator: KeywordOperatorType): void => {
    const newFilters = { ...keywordFilters }
    newFilters[operator] = null
    updateKeywordFilters(newFilters)
  }

  // Get keyword filter selection for a specific operator
  const getKeywordFilterSelection = (
    operator: KeywordOperatorType,
  ): {
    field: string
    operator: KeywordOperatorType
    values: string[]
  } | null => {
    return keywordSelections[operator] || null
  }

  // Create a keyword filter option for a specific operator
  const createKeywordFilter = (
    operator: KeywordOperatorType,
  ): FilterOption => ({
    field: `keywords-${operator}`,
    alias: 'Keywords',
    filter_type: FilterTypes.SELECT,
    values: [],
  })

  // Handler for Apply inside the popover's Keywords pane. The pane edits all
  // of its rows before committing, so this replaces every operator at once
  // (the pane's payload carries `null` for any operator with no row).
  // `useCallback` keeps the prop identity stable for FilterSelectHeader's
  // memo barrier.
  const handleKeywordFiltersApply = useCallback(
    (filters: { [key in KeywordOperatorType]?: string[] | null }): void => {
      updateKeywordFilters({ ...keywordFilters, ...filters })
    },
    [keywordFilters, updateKeywordFilters],
  )

  // Handler for Clear inside the popover's Keywords pane — removes every
  // keyword operator at once (same field-removal semantics as
  // `handleKeywordFilterDelete`, batched).
  const handleKeywordFiltersClear = useCallback((): void => {
    const cleared: { [key in KeywordOperatorType]?: null } = {}
    KEYWORD_OPERATORS.forEach((operator) => {
      cleared[operator] = null
    })
    updateKeywordFilters({ ...keywordFilters, ...cleared })
  }, [keywordFilters, updateKeywordFilters])

  // Handler for Apply inside the popover's Smart search pane. Clear reuses
  // `handleRemoveSmartSearch` directly.
  const handleSmartSearchApply = useCallback(
    (value: string): void => {
      debouncedUpdateSmartSearch.cancel()
      updateSmartSearch(value)
    },
    [updateSmartSearch, debouncedUpdateSmartSearch],
  )

  // Handler to remove smart search
  const handleRemoveSmartSearch = (): void => {
    // Set to null to hide the card completely
    debouncedUpdateSmartSearch.cancel()
    updateSmartSearch(null)
  }

  // Extract numeric filters from selectedFilterOptions (read directly from store).
  // Exclude sentiment_filter — rendered inside SentimentFilterCard, not as a standalone NumericFilterCard.
  const numericFilters = useMemo(() => {
    return selectedFilterOptions.filter(
      (filter) =>
        isNumericFilterField(filter.field) &&
        filter.field !== 'sentiment_filter',
    )
  }, [selectedFilterOptions])

  // ── Sentiment state ────────────────────────────────────────────────────
  // Derive the active state for the unified SentimentFilterCard.
  // NOTE: sentimentSelection / postSentimentSelection / sentimentFilterNumeric
  // are declared early above (before handleFilterAdd) so the exclusivity guard
  // can reference them in the callback.

  const sentimentCategoricalMode: SentimentMode = deriveSentimentMode(
    sentimentSelection,
    postSentimentSelection,
  )

  const sentimentCategoricalValues: string[] = useMemo(
    () =>
      sentimentCategoricalMode === 'record'
        ? (postSentimentSelection?.values ?? [])
        : (sentimentSelection?.values ?? []),
    [sentimentCategoricalMode, postSentimentSelection, sentimentSelection],
  )

  // Show the card when any of the three fields carries state. Entry existence
  // (not just values length) is checked for the categorical fields so a
  // Record-mode marker (empty post_sentiment entry) keeps the card visible
  // when no categorical values are selected (e.g. numeric-only filter).
  const hasSentimentFilter =
    sentimentSelection !== null ||
    postSentimentSelection !== null ||
    sentimentFilterNumeric !== undefined

  // The sentiment FilterOption (for the value checkbox list in FilterPopover)
  const sentimentFilterOption =
    restrictedFilterOptions.find((f) => f.field === 'sentiment') ?? null

  // ── Inherited sentiment (read-only) ─────────────────────────────────────
  // Mirrors the derivation above, sourced from the parent scope's own
  // sentiment/post_sentiment facets instead of this tile's. Numeric
  // (sentiment_filter) never appears here — a dashboard's global filters are
  // v1-shaped, and v1 can't express it (see charts-v2/AGENTS.md).
  const inheritedSentimentFacet =
    inherited?.facets.find((f) => f.selection.field === 'sentiment') ?? null
  const inheritedPostSentimentFacet =
    inherited?.facets.find((f) => f.selection.field === 'post_sentiment') ??
    null
  const hasInheritedSentiment =
    inheritedSentimentFacet !== null || inheritedPostSentimentFacet !== null
  const inheritedSentimentCategoricalMode: SentimentMode = deriveSentimentMode(
    inheritedSentimentFacet?.selection ?? null,
    inheritedPostSentimentFacet?.selection ?? null,
  )
  const inheritedSentimentCategoricalValues: string[] =
    inheritedSentimentCategoricalMode === 'record'
      ? (inheritedPostSentimentFacet?.selection.values ?? [])
      : (inheritedSentimentFacet?.selection.values ?? [])
  const inheritedSentimentSupersededNote =
    (inheritedSentimentCategoricalMode === 'record'
      ? inheritedPostSentimentFacet
      : inheritedSentimentFacet
    )?.supersededNote ?? null

  // ── Sentiment handlers ─────────────────────────────────────────────────

  const handleSentimentModeChange = useCallback(
    (newMode: SentimentMode): void => {
      const oldField =
        sentimentCategoricalMode === 'record' ? 'post_sentiment' : 'sentiment'
      const newField = newMode === 'record' ? 'post_sentiment' : 'sentiment'
      if (oldField === newField) return

      const currentVals = sentimentCategoricalValues
      if (currentVals.length > 0) {
        // Move categorical values to the new field and remove the old entry.
        updateFilterSelection(newField, 'is', currentVals)
        if (
          (oldField === 'sentiment' && sentimentSelection) ||
          (oldField === 'post_sentiment' && postSentimentSelection)
        ) {
          updateFilterSelection(oldField, 'is', [])
        }
      } else {
        // No categorical values (e.g. numeric-only filter). Persist the mode
        // choice as an empty store entry: an empty post_sentiment entry is the
        // Record marker; absence of both categorical entries = Sentence default.
        // The store creates an entry on write-with-empty-values (add path) and
        // removes it on delete (empty values + no rangeData) — both are
        // intentional here.
        if (newMode === 'record') {
          // Create the Record marker only when no entry exists yet.
          if (!postSentimentSelection) {
            updateFilterSelection('post_sentiment', 'is', [])
          }
        } else {
          // Remove the Record marker if present; also clean up any stale empty
          // sentiment entry.
          if (postSentimentSelection) {
            updateFilterSelection('post_sentiment', 'is', [])
          }
          if (sentimentSelection) {
            updateFilterSelection('sentiment', 'is', [])
          }
        }
      }
    },
    [
      sentimentCategoricalMode,
      sentimentCategoricalValues,
      sentimentSelection,
      postSentimentSelection,
      updateFilterSelection,
    ],
  )

  const handleSentimentCategoricalChange = useCallback(
    (values: string[]): void => {
      const activeField =
        sentimentCategoricalMode === 'record' ? 'post_sentiment' : 'sentiment'
      // Enforce sentiment exclusivity: setting categorical values clears numeric.
      if (values.length > 0 && sentimentFilterNumeric) {
        updateFilterSelection('sentiment_filter', 'greater than', [])
      }
      updateFilterSelection(activeField, 'is', values)
    },
    [sentimentCategoricalMode, sentimentFilterNumeric, updateFilterSelection],
  )

  const handleSentimentNumericUpdate = useCallback(
    (operator: NumericOperatorType, value: number | undefined): void => {
      if (value === undefined) {
        // Delete the numeric filter only if it actually exists
        if (sentimentFilterNumeric) {
          updateFilterSelection('sentiment_filter', 'greater than', [])
        }
      } else {
        // Enforce sentiment exclusivity: setting numeric clears categorical.
        if (sentimentSelection) {
          updateFilterSelection('sentiment', 'is', [])
        }
        if (postSentimentSelection) {
          updateFilterSelection('post_sentiment', 'is', [])
        }
        updateFilterSelection('sentiment_filter', operator, [], {
          numericValue: value,
        })
      }
    },
    [
      sentimentFilterNumeric,
      sentimentSelection,
      postSentimentSelection,
      updateFilterSelection,
    ],
  )

  const handleSentimentDelete = useCallback((): void => {
    if (sentimentSelection) {
      updateFilterSelection('sentiment', 'is', [])
    }
    if (postSentimentSelection) {
      updateFilterSelection('post_sentiment', 'is', [])
    }
    if (sentimentFilterNumeric) {
      updateFilterSelection('sentiment_filter', 'greater than', [])
    }
  }, [
    sentimentSelection,
    postSentimentSelection,
    sentimentFilterNumeric,
    updateFilterSelection,
  ])

  // Handler for Apply inside the popover's Sentiment pane. Unlike Rating (one
  // write), Sentiment's committed state spans three fields — categorical
  // (sentiment/post_sentiment) and numeric (sentiment_filter) — so a single
  // Apply has to pick exactly one of {categorical, numeric, mode-only marker}
  // and clear the other two, mirroring the exclusivity rules the granular
  // handlers above already enforce.
  const handleSentimentPaneApply = useCallback(
    (draft: {
      mode: SentimentMode
      values: string[]
      numericOperator: NumericOperatorType
      numericValue: number | undefined
    }): void => {
      const { mode, values, numericOperator, numericValue } = draft
      if (values.length > 0) {
        const activeField = mode === 'record' ? 'post_sentiment' : 'sentiment'
        updateFilterSelection(activeField, 'is', values)
        if (mode === 'record') {
          if (sentimentSelection) updateFilterSelection('sentiment', 'is', [])
        } else if (postSentimentSelection) {
          updateFilterSelection('post_sentiment', 'is', [])
        }
        if (sentimentFilterNumeric) {
          updateFilterSelection('sentiment_filter', 'greater than', [])
        }
      } else if (numericValue !== undefined) {
        if (sentimentSelection) updateFilterSelection('sentiment', 'is', [])
        if (postSentimentSelection) {
          updateFilterSelection('post_sentiment', 'is', [])
        }
        updateFilterSelection('sentiment_filter', numericOperator, [], {
          numericValue,
        })
      } else {
        // Mode-only: no categorical values, no numeric value. Persist the
        // mode choice via the post_sentiment marker (see
        // handleSentimentModeChange above for the same convention).
        if (sentimentFilterNumeric) {
          updateFilterSelection('sentiment_filter', 'greater than', [])
        }
        if (sentimentSelection) {
          updateFilterSelection('sentiment', 'is', [])
        }
        if (mode === 'record') {
          if (!postSentimentSelection) {
            updateFilterSelection('post_sentiment', 'is', [])
          }
        } else if (postSentimentSelection) {
          updateFilterSelection('post_sentiment', 'is', [])
        }
      }
    },
    [
      sentimentSelection,
      postSentimentSelection,
      sentimentFilterNumeric,
      updateFilterSelection,
    ],
  )

  // Handler for Apply inside the popover's Records/Sentences pane.
  const handleNumericFilterApply = useCallback(
    (
      field: NumericFilterFieldType,
      operator: NumericOperatorType,
      value: number,
    ): void => {
      updateFilterSelection(field, operator, [], { numericValue: value })
    },
    [updateFilterSelection],
  )

  // Handler to update numeric filter operator
  const handleNumericOperatorChange = (
    field: NumericFilterFieldType,
    operator: NumericOperatorType,
  ): void => {
    const currentFilter = selectedFilterOptions.find((f) => f.field === field)
    if (currentFilter) {
      updateFilterSelection(field, operator, [], {
        numericValue: currentFilter.numericValue,
      })
    }
  }

  // Handler to update numeric filter value
  const handleNumericValueChange = (
    field: NumericFilterFieldType,
    value: number | undefined,
  ): void => {
    const currentFilter = selectedFilterOptions.find((f) => f.field === field)
    if (currentFilter) {
      updateFilterSelection(
        field,
        currentFilter.operator as NumericOperatorType,
        [],
        { numericValue: value },
      )
    }
  }

  // Handler to delete numeric filter
  const handleNumericFilterDelete = (field: NumericFilterFieldType): void => {
    // Remove filter by calling updateFilterSelection with empty values and no rangeData
    updateFilterSelection(field, 'greater than', [])
  }

  // Get label for numeric filter field
  const getNumericFilterLabel = (field: NumericFilterFieldType): string => {
    return NUMERIC_FILTER_LABELS[field]
  }

  // Every card kind this section can render is empty — smart search, keyword,
  // numeric, and sentiment cards all live outside `filterEntries`, so that
  // alone isn't a valid "no filters" signal. Inherited smart search/keywords
  // aren't reflected in `filterEntries` either (only the facet ones are), so
  // they're checked explicitly too. Suppressed while the skeleton is
  // showing so the empty state doesn't flash over a loading list.
  const hasNoFilterCards =
    filterEntries.length === 0 &&
    !showSmartSearch &&
    !inherited?.smartSearch &&
    Object.keys(keywordSelections).length === 0 &&
    !inherited?.keywords &&
    numericFilters.length === 0 &&
    !hasSentimentFilter &&
    !hasInheritedSentiment
  const showAddFiltersEmptyState =
    hasNoFilterCards &&
    !(isFilterOptionsLoading && selectedFilterOptions.length > 0)

  const filterSelectHeader = (
    <FilterSelectHeader
      options={restrictedFilterOptions}
      isLoading={isFilterOptionsLoading}
      onSelect={handleFilterAdd}
      getCurrentSelectionsForField={getFilterSelection}
      keywordFilters={keywordFilters}
      smartSearchValue={smartSearchValue}
      onKeywordFiltersApply={handleKeywordFiltersApply}
      onKeywordFiltersClear={handleKeywordFiltersClear}
      onSmartSearchApply={handleSmartSearchApply}
      onSmartSearchClear={handleRemoveSmartSearch}
      onNumericFilterApply={handleNumericFilterApply}
      onNumericFilterClear={handleNumericFilterDelete}
      onSentimentApply={handleSentimentPaneApply}
      onSentimentClear={handleSentimentDelete}
      onSelectedFieldChange={onSelectedFieldChange}
      onSavedFilterApply={onSavedFilterApply}
      onSavedQueryApply={onSavedQueryApply}
      showSyncButton={showSyncButton}
      onSyncFilters={onSyncFilters}
      portalTarget={headerActionsPortalTarget}
      hideHeaderRow={hideFilterHeaderRow}
      showEmptyState={alwaysShowAddFilters || showAddFiltersEmptyState}
      emptyStateLabel={emptyStateLabel}
      emptyStateVariant={emptyStateVariant}
      popoverAnchorMode={filterPopoverAnchorMode}
      popoverPortalTarget={filterPopoverPortalTarget}
      restriction={restriction}
      fieldsOnlySidebar={filterPopoverFieldsOnlySidebar}
      disabledFields={filterPopoverDisabledFields}
      onApplySelections={applyFilterSelections}
      externalTrigger={externalTrigger}
    />
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {addFiltersRowPosition === 'before' && filterSelectHeader}
      {!hideCardList && !externalTrigger && (
        <>
          {isFilterOptionsLoading && filterEntries.length === 0 && (
            <FilterCardsSkeleton count={selectedFilterOptions.length} />
          )}

          {staleValuesNotice && (
            <StaleFilterValuesNotice {...staleValuesNotice} />
          )}

          {filterEntries.length > 0 && (
            <SelectedFilterOptionList
              entries={filterEntries}
              updateFilterSelection={updateFilterSelection}
              getFilterSelection={getFilterSelection}
              ghost={
                getGhostSelection && onToggleFilterDisabled && onRemoveGhost
                  ? { getGhostSelection, onToggleFilterDisabled, onRemoveGhost }
                  : undefined
              }
              parentScope={{
                sourceLabel: inherited?.sourceLabel ?? '',
                onEditSource: onEditInheritedSource,
                tileKind,
              }}
              getStaleInfo={getStaleInfo}
              FilterCard={FilterCard}
              presentation={{
                showBreakdownHierarchy,
                lockOperator,
                maxHeight: listMaxHeight,
                getValueLabel,
              }}
            />
          )}

          {/* Inherited Smart Search from the parent scope (dashboard), read-only —
          rendered before the tile's own so global precedes local. */}
          {inherited?.smartSearch && (
            <EditableTextFilterCard
              title="Smart Search"
              groupLabel={groupLabels?.search}
              value={inherited.smartSearch.value}
              placeholder="Type a phrase to search..."
              onChange={() => {}}
              onClear={() => {}}
              removeable={false}
              inherited={{
                supersededNote: inherited.smartSearch.supersededNote,
                sourceLabel: inherited.sourceLabel,
              }}
              accent="global"
              tileKind={tileKind}
              onEditInheritedSource={onEditInheritedSource}
            />
          )}

          {/* Smart Search - shown inline when activated */}
          {showSmartSearch && (
            <EditableTextFilterCard
              title="Smart Search"
              groupLabel={groupLabels?.search}
              value={smartSearchInput}
              placeholder="Type a phrase to search..."
              onChange={(next) => {
                setSmartSearchInput(next)
                debouncedUpdateSmartSearch(next)
              }}
              onClear={handleRemoveSmartSearch}
              onCancelEdit={() => setSmartSearchInput(smartSearchValue ?? '')}
              autoEditWhenEmpty
              clearTitle="Remove smart search"
              removeable
              accent={ownedRowAccent}
              tileKind={tileKind}
              onEditInheritedSource={onEditInheritedSource}
            />
          )}

          {/* Inherited keyword cards from the parent scope (dashboard), read-only —
          rendered before the tile's own so global precedes local. */}
          {inherited?.keywords &&
            Object.entries(inherited.keywords.criteria).map(
              ([operator, values]) => (
                <KeywordCard
                  key={`inherited-${operator}`}
                  groupLabel={groupLabels?.search}
                  filter={createKeywordFilter(operator as KeywordOperatorType)}
                  updateFilterSelection={() => {}}
                  getFilterSelection={() => ({
                    field: `keywords-${operator}`,
                    operator: operator as KeywordOperatorType,
                    values,
                  })}
                  inherited={{
                    supersededNote: inherited.keywords!.supersededNote,
                    sourceLabel: inherited.sourceLabel,
                  }}
                  accentBar={{
                    accent: 'global',
                    tileKind,
                    onEditSource: onEditInheritedSource,
                  }}
                />
              ),
            )}

          {/* Render all active keyword cards */}
          {Object.entries(keywordSelections).map(([operator]) => (
            <KeywordCard
              key={operator}
              groupLabel={groupLabels?.search}
              filter={createKeywordFilter(operator as KeywordOperatorType)}
              updateFilterSelection={(
                field: string,
                newOperator: KeywordOperatorType,
                values: string[],
              ) =>
                handleKeywordFilterUpdate(
                  field,
                  newOperator,
                  values,
                  operator as KeywordOperatorType,
                )
              }
              getFilterSelection={(): {
                field: string
                operator: KeywordOperatorType
                values: string[]
              } | null =>
                getKeywordFilterSelection(operator as KeywordOperatorType)
              }
              onDelete={() =>
                handleKeywordFilterDelete(operator as KeywordOperatorType)
              }
              accentBar={
                ownedRowAccent
                  ? {
                      accent: ownedRowAccent,
                      tileKind,
                      onEditSource: onEditInheritedSource,
                    }
                  : undefined
              }
            />
          ))}

          {/* Render all active numeric filter cards (sentiment_filter excluded — see SentimentFilterCard below) */}
          {numericFilters.map((filter) => (
            <NumericFilterCard
              key={filter.field}
              groupLabel={groupLabels?.threshold}
              field={filter.field as NumericFilterFieldType}
              label={getNumericFilterLabel(
                filter.field as NumericFilterFieldType,
              )}
              operator={filter.operator as NumericOperatorType}
              value={filter.numericValue}
              onOperatorChange={(operator) =>
                handleNumericOperatorChange(
                  filter.field as NumericFilterFieldType,
                  operator,
                )
              }
              onValueChange={(value) =>
                handleNumericValueChange(
                  filter.field as NumericFilterFieldType,
                  value,
                )
              }
              onDelete={() =>
                handleNumericFilterDelete(
                  filter.field as NumericFilterFieldType,
                )
              }
            />
          ))}

          {/* Inherited Sentiment card from the parent scope (dashboard), read-only —
          rendered before the tile's own so global precedes local. */}
          {hasInheritedSentiment && (
            <SentimentFilterCard
              sentimentFilterOption={sentimentFilterOption}
              groupLabel={groupLabels?.language}
              categoricalMode={inheritedSentimentCategoricalMode}
              categoricalValues={inheritedSentimentCategoricalValues}
              numericOperator={null}
              numericValue={undefined}
              onModeChange={() => {}}
              onCategoricalChange={() => {}}
              onNumericChange={() => {}}
              onDelete={() => {}}
              inherited={{
                supersededNote: inheritedSentimentSupersededNote,
                sourceLabel: inherited?.sourceLabel ?? '',
              }}
              accent="global"
              tileKind={tileKind}
              onEditInheritedSource={onEditInheritedSource}
            />
          )}

          {/* Unified Sentiment card — shown when any sentiment filter is active */}
          {hasSentimentFilter && (
            <SentimentFilterCard
              sentimentFilterOption={sentimentFilterOption}
              groupLabel={groupLabels?.language}
              categoricalMode={sentimentCategoricalMode}
              categoricalValues={sentimentCategoricalValues}
              numericOperator={
                sentimentFilterNumeric
                  ? (sentimentFilterNumeric.operator as NumericOperatorType)
                  : null
              }
              numericValue={sentimentFilterNumeric?.numericValue}
              onModeChange={handleSentimentModeChange}
              onCategoricalChange={handleSentimentCategoricalChange}
              onNumericChange={handleSentimentNumericUpdate}
              onDelete={handleSentimentDelete}
              accent={ownedRowAccent}
              tileKind={tileKind}
              onEditInheritedSource={onEditInheritedSource}
            />
          )}
        </>
      )}
      {addFiltersRowPosition === 'after' && filterSelectHeader}
    </div>
  )
}
